/* src/pages/DentalITServices.js */

import React from "react";
import PageSection from "../components/PageSection";
import { Helmet } from "react-helmet-async";
import styles from "./Pages.module.css";
import { Link } from "react-router-dom";
import { FaCheckCircle, FaLaptop, FaHdd, FaLock, FaWifi } from "react-icons/fa";

function DentalITServices() {
  const faqs = [
    {
      question: "Do you work with specific dental software like Eaglesoft or Dentrix?",
      answer:
        "Yes. I've worked with practices using Eaglesoft, Dentrix, Carestream, and other common dental practice management and imaging software. I handle the IT side, network setup, workstation performance, backups, and data access. For software-specific issues, you'd work with the software vendor's support, but I make sure your systems are set up to run their software reliably.",
    },
    {
      question: "Are you a HIPAA compliance consultant?",
      answer:
        "No. I handle the IT side of HIPAA compliance, secure backups, access controls, encrypted storage, and audit practices. For full compliance documentation and auditing, you'll want a dedicated HIPAA compliance specialist. I make sure your systems are solid; they make sure your documentation is airtight.",
    },
    {
      question: "What happens if our imaging system goes down?",
      answer:
        "That's exactly why backups and redundancy matter. If you have proper backups in place, a workstation failure isn't a disaster, it's a minor inconvenience while I get the system back online or restore from backup. If you don't have backups yet, the IT Health Check will show you where the risk is.",
    },
    {
      question: "How does your IT support work with our practice?",
      answer:
        "You can use break-fix support (call when something breaks) or a monthly membership plan. With membership, you get regular maintenance, backup checks, and priority scheduling. Either way, you work directly with Austin, not a call center.",
    },
  ];

  return (
    <PageSection title="IT Support for Dental Offices in Shreveport-Bossier" cta faqs={faqs}>
      <Helmet>
        <title>IT Support for Dental Offices in Shreveport-Bossier | Tharpe Industries</title>
        <meta
          name="description"
          content="Keep your dental practice running smoothly. IT support for Eaglesoft, Dentrix, imaging systems, and patient data in Shreveport and Bossier City."
        />
        <meta
          name="keywords"
          content="dental practice IT support Shreveport, Eaglesoft support, Dentrix support, dental office network, HIPAA IT support dental"
        />
        <link rel="canonical" href="https://tharpeindustries.com/it-support-dental-offices" />
      </Helmet>

      <div className={styles.contentContainer}>
        <div className={styles.textContent}>
          <h2>Keep your imaging systems and patient data running smoothly.</h2>
          <div className={styles.heroQuickCta}>
            <Link to="/it-health-check" className={styles.heroQuickCtaBtn}>Book an IT Health Check, $149 →</Link>
          </div>
          <p>
            Dental practices depend on reliable IT, imaging software, practice management systems, patient records, and front desk operations all need to work together without interruption. Downtime doesn't just slow things down; it means patients waiting, appointments delayed, and patient records inaccessible.
          </p>
          <p>
            Austin Tharpe helps Shreveport-Bossier dental practices keep their systems running reliably. I handle network performance, workstation maintenance, backup systems configured for patient data, and the IT basics of HIPAA compliance.
          </p>

          <h3>Why dental practices have unique IT needs</h3>
          <ul>
            <li><strong>Dental imaging software requires stable workstations:</strong> Eaglesoft, Dentrix, Carestream, and other practice management systems need reliable hardware and fast network connections. Slow computers or dropped networks frustrate staff and impact patient care.</li>
            <li><strong>Patient records and X-rays can't afford downtime:</strong> Your imaging and records are critical. If systems go down, you can't access patient history, take new X-rays, or schedule appointments. That's a revenue hit and a patient experience problem.</li>
            <li><strong>HIPAA compliance requires proper data handling:</strong> Patient records are protected health information. You need secure backups, access controls so only authorized staff can access patient data, and basic audit practices. It's the law, and it protects your practice.</li>
            <li><strong>Multiple systems need to work together:</strong> Front desk computers, operatory workstations, imaging systems, and backup systems all need to be in sync. If one isn't working right, the whole practice feels it.</li>
          </ul>

          <h3>What Tharpe Industries handles for dental practices</h3>
          <ul>
            <li><strong>Workstation maintenance and performance:</strong> Slow computers? Network hiccups between operatories? Dead Wi-Fi spots? I handle diagnostics and fixes, keeping your imaging software and practice management systems running at full speed.</li>
            <li><strong>Backup systems configured for patient data:</strong> Your patient records and imaging files need to be backed up automatically so you're never starting over from zero if something fails. I set that up and verify it actually works.</li>
            <li><strong>HIPAA-aligned IT practices:</strong> I implement access controls (so only authorized staff can access patient records), encrypted storage for sensitive data, basic audit practices, and secure backup procedures. I'm not a compliance auditor, but I make sure your IT foundation is solid.</li>
            <li><strong>Network reliability:</strong> Slow connections between operatories? Spotty Wi-Fi in certain rooms? I handle network diagnostics and configuration so your practice stays connected.</li>
            <li><strong>New workstation setup and software installation:</strong> Need a new computer in an operatory? I handle the hardware, software install, and integration with your practice management system.</li>
          </ul>

          <h3>I'm the IT side, not the compliance consultants</h3>
          <p>
            Tharpe Industries isn't a HIPAA compliance consultant, I handle the IT side of the equation. I make sure your systems, backups, access controls, and data storage practices are solid from a technology standpoint. For full HIPAA compliance auditing, documentation, and legal review, you'll want to work with a dedicated compliance specialist who can review your entire practice operations and documentation. Think of it this way: I make sure your IT systems are secure; they make sure your documentation and processes are compliant across the whole practice.
          </p>

          <div className={styles.ctaContainer}>
            <p><strong>Ready to see where your practice's IT actually stands?</strong></p>
            <p className={styles.emergencyText}>Start with the $149 IT Health Check. I'll review your current setup, imaging software performance, backups, and data handling practices, then give you a clear picture of what's working and what needs attention.</p>
            <Link to="/it-health-check" className={styles.ctaButton}>Start with the IT Health Check</Link>
          </div>

          <div className={styles.secondaryCta}>
            <p>Have questions about your practice's specific setup? <Link to="/contact-us">Send me a message</Link> and I'll help you think through what you need.</p>
          </div>

          <div style={{ marginTop: '2rem', paddingTop: '1.5rem', borderTop: '1px solid rgba(255,255,255,0.1)' }}>
            <p style={{ fontSize: '0.9rem', color: 'var(--text-muted, #9ca3af)', marginBottom: '0.75rem' }}>I also serve other local businesses:</p>
            <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexWrap: 'wrap', gap: '0.5rem 1.5rem' }}>
              <li><Link to="/it-support-medical-offices" style={{ fontSize: '0.9rem' }}>Medical Offices</Link></li>
              <li><Link to="/it-support-law-firms" style={{ fontSize: '0.9rem' }}>Law Firms</Link></li>
              <li><Link to="/it-support-accounting-firms" style={{ fontSize: '0.9rem' }}>Accounting Firms</Link></li>
              <li><Link to="/it-support-contractors" style={{ fontSize: '0.9rem' }}>Contractors & Trades</Link></li>
              <li><Link to="/it-support-restaurants" style={{ fontSize: '0.9rem' }}>Restaurants</Link></li>
            </ul>
          </div>
        </div>
      </div>
    </PageSection>
  );
}

export default DentalITServices;
